import axios from 'axios';
import { getAPI } from 'utils/helpers';
import * as Sentry from 'utils/errorTracking';
import apiClient from 'lib/apiClient';

export const TIMEOUT_MS = 5000;

export const clipByIdUrl = (clipId: number): string => `${getAPI()}/campaign/clips/${clipId}`;

export const isValidClipId = (clipId: number): boolean =>
  Number.isSafeInteger(clipId) && clipId > 0;

export const parseClipId = (id: string): number | null => {
  if (!/^\d+$/.test(id)) return null;
  const clipId = Number(id);
  return isValidClipId(clipId) ? clipId : null;
};

export interface ClipOffering {
  id: string;
  primaryOfferingId: string | null;
  name: string;
  description: string | null;
  status: string;
  slug: string;
  logoUrl: string | null;
}

export interface Clip {
  id: number;
  offering: ClipOffering;
  title: string;
  description: string | null;
  approvedAt: string | null;
  duration: number;
  aspectRatio: string | null;
  playbackId: string | null;
  playbackUrl: string | null;
  thumbnailUrl: string | null;
  createdDate: string;
  likeCount: number;
  likedByMe: boolean;
}

export interface ClipLike {
  clipId: number;
  likeCount: number;
  likedByMe: boolean;
}

export interface ClipSSRResult {
  clip: Clip | null;
  isNotFound: boolean;
}

export interface ClipsResponse {
  list: Clip[];
  pageNumber: number;
  totalElements: number;
  pageSize: number;
  totalPages: number;
}

export type ClipsSort = 'approvedAt' | 'createdDate' | 'duration';
export type ClipsSortDirection = 'asc' | 'desc';

export interface GetClipsParams {
  offeringId?: string;
  sort?: ClipsSort;
  direction?: ClipsSortDirection;
  page?: number;
  size?: number;
}

const EMPTY: ClipsResponse = {
  list: [],
  pageNumber: 0,
  totalElements: 0,
  pageSize: 0,
  totalPages: 0,
};

const buildQueryString = (params: GetClipsParams): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * Client-only: the sole caller is useGetClips, a React Query hook used inside
 * 'use client' components. If a future server-side caller appears, add a ky
 * SSR branch and a matching test.
 *
 * The endpoint is public, but apiClient attaches the JWT when the user is
 * logged in so the backend can populate per-user fields (e.g. likedByMe).
 */
export const getPublicClips = async (params: GetClipsParams = {}): Promise<ClipsResponse> => {
  const url = `${getAPI()}/campaign/clips${buildQueryString(params)}`;

  try {
    const { data } = await apiClient.get<ClipsResponse>(url, {
      timeout: TIMEOUT_MS,
    });
    return data ?? EMPTY;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (!status) {
      Sentry.captureMessage('Network error for public clips', {
        level: 'warning',
        fingerprint: ['network-error-clips'],
      });
    } else if (status >= 500) {
      Sentry.captureMessage('Gateway 5xx for public clips', {
        level: 'warning',
        fingerprint: ['gateway-5xx-clips'],
        extra: { status },
      });
    } else if (status !== 404) {
      Sentry.captureException(error);
    }
    throw error;
  }
};

/**
 * Client-only: used by the shareable clip route. The endpoint is public, but
 * apiClient attaches the JWT when available so likedByMe is accurate.
 */
export const getPublicClip = async (clipId: number): Promise<Clip> => {
  let data: Clip | undefined;
  try {
    ({ data } = await apiClient.get<Clip>(clipByIdUrl(clipId), {
      timeout: TIMEOUT_MS,
    }));
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (!status) {
      Sentry.captureMessage('Network error for public clip', {
        level: 'warning',
        fingerprint: ['network-error-clip'],
      });
    } else if (status >= 500) {
      Sentry.captureMessage('Gateway 5xx for public clip', {
        level: 'warning',
        fingerprint: ['gateway-5xx-clip'],
        extra: { status },
      });
    } else if (status !== 404) {
      Sentry.captureException(error);
    }
    throw error;
  }

  if (!data) {
    Sentry.captureMessage('Empty body for public clip', {
      level: 'warning',
      fingerprint: ['empty-body-clip'],
    });
    throw new Error('Empty response body for public clip');
  }
  return data;
};

/**
 * Server-side variant of getPublicClip for the shareable clip route. A
 * server-side fetch does not inherit the browser's cookies, so the request's
 * cookie header is forwarded explicitly (mirroring the SSR pattern in
 * services/offerings.ts: getUserFromCookie / checkRestrictedAccess). The
 * gateway reads it to populate per-user fields (e.g. likedByMe). Uses plain
 * axios rather than apiClient, whose in-memory Bearer token is client-only.
 * Returns a typed result so callers can render a 404 for missing clips while
 * retaining a recoverable fallback for other request failures.
 */
export const getClipSSR = async ({
  clipId,
  cookie,
}: {
  clipId: number;
  cookie?: string;
}): Promise<ClipSSRResult> => {
  try {
    const { data } = await axios.get<Clip>(clipByIdUrl(clipId), {
      headers: cookie ? { cookie } : {},
      withCredentials: !!cookie,
      timeout: TIMEOUT_MS,
    });
    if (!data) {
      Sentry.captureMessage('Empty body for public clip', {
        level: 'warning',
        fingerprint: ['empty-body-clip'],
      });
      return { clip: null, isNotFound: false };
    }
    return { clip: data, isNotFound: false };
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (!status) {
      Sentry.captureMessage('Network error for public clip', {
        level: 'warning',
        fingerprint: ['network-error-clip'],
      });
    } else if (status >= 500) {
      Sentry.captureMessage('Gateway 5xx for public clip', {
        level: 'warning',
        fingerprint: ['gateway-5xx-clip'],
        extra: { status },
      });
    } else if (status !== 404) {
      Sentry.captureException(error);
    }
    return { clip: null, isNotFound: status === 404 };
  }
};

/**
 * Client-only: called from the useClipLike mutation hook. Requires a JWT
 * (apiClient attaches it). Idempotent server-side, so re-issuing a like the
 * user already holds returns the same DTO.
 *
 * Unlike getPublicClips, we do not swallow/bucket errors here: like failures
 * are user-visible (the optimistic update rolls back), so the mutation surfaces
 * them rather than reporting silent background reads to Sentry.
 */
export const likeClip = async (clipId: number): Promise<ClipLike> => {
  const { data } = await apiClient.post<ClipLike>(
    `${getAPI()}/campaign/clips/${clipId}/likes`,
    null,
    { timeout: TIMEOUT_MS },
  );
  return data;
};

export const unlikeClip = async (clipId: number): Promise<ClipLike> => {
  const { data } = await apiClient.delete<ClipLike>(`${getAPI()}/campaign/clips/${clipId}/likes`, {
    timeout: TIMEOUT_MS,
  });
  return data;
};
